import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Dropdown,
  Empty,
  Flex,
  Input,
  message,
  Popover,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  FilterOutlined,
  LinkedinFilled,
  MailOutlined,
  PlusOutlined,
  SearchOutlined,
  ShareAltOutlined,
  SwapOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  UserAddOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useFieldConfig } from '../hooks/useDropdownFields';
import { useMenuPermission } from '../hooks/useMenuPermission';
import { getModuleDataList } from '../services/dropdownApi';
import { mapListResponse } from '../services/responseMapping/mapListResponse';
import { fetchJsonWithAuth } from '../services/authApi';
import { AUTH_URL } from '../services/apiConfig';
import AppBreadcrumb from './Breadcrumb';
import CustomPagination from './CustomPagination';
import DynamicTabs from './DynamicTabs';
import ViewSummaryWidget from './ViewSummaryWidget';
import DynamicFieldFilter from './filters/DynamicFieldFilter';
import eyeOutlinedIcon from './images/common/eyeoutlined.svg';
import frameIcon from './images/common/frame.svg';
import unorderedListOutlinedIcon from './images/common/unorderedlistoutlined.svg';

const { Text } = Typography;

function getFieldLabel(field) {
  return firstPresentValue(field?.label, field?.fieldLabel, field?.name, field?.fieldName, field?.value, field?.field);
}

function getFieldValue(field) {
  return firstPresentValue(field?.value, field?.field, field?.fieldName, field?.name, field?.label);
}

function firstPresentValue(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function getNestedRecordValue(record, fieldKey) {
  if (!fieldKey || typeof fieldKey !== 'string') return undefined;

  return fieldKey.split('.').reduce((value, key) => value?.[key], record);
}

function getRecordRawValue(record, fieldKey) {
  if (fieldKey === 'id') return record?.id ?? record?._id;

  const directValue = record?.[fieldKey];

  return directValue ?? getNestedRecordValue(record, fieldKey);
}

function isExternalSourceValue(value) {
  if (Array.isArray(value)) return value.some(isExternalSourceValue);
  if (value === null || value === undefined) return false;

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return false;

  return !['all', 'internal'].includes(normalized);
}

function getExternalProfileSource(record) {
  const flatProfileKey = Object.keys(record ?? {}).find((key) => (
    /^customFields\.[^.]*profiledata\./i.test(key)
    && !/^customFields\.internalprofiledata\./i.test(key)
  ));

  if (flatProfileKey) {
    return flatProfileKey
      .replace(/^customFields\./i, '')
      .replace(/profiledata\..*$/i, '');
  }

  const customFields = record?.customFields;
  if (!customFields || typeof customFields !== 'object') return '';

  const profileKey = Object.keys(customFields).find((key) => (
    /profiledata$/i.test(key)
    && !/^internalprofiledata$/i.test(key)
    && customFields[key]
    && typeof customFields[key] === 'object'
  ));

  return profileKey ? profileKey.replace(/profiledata$/i, '') : '';
}

function isExternalSourceField(field) {
  if (typeof field?.isVisible === 'boolean' && !field.isVisible) return false;
  if (typeof field?.isVisible === 'string' && field.isVisible.toLowerCase() === 'false') return false;

  const fieldKey = getFieldValue(field);
  if (!fieldKey || typeof fieldKey !== 'string') return false;

  return (
    /^customFields\.[^.]*profiledata\./i.test(fieldKey)
    && !/^customFields\.internalprofiledata\./i.test(fieldKey)
  );
}

function isExternalSourceRecord(record) {
  const explicitSources = [
    record?.sourceType,
    record?.profileSource,
    record?.selectedSource,
    record?.customFields?.sourceType,
    record?.customFields?.profileSource,
  ];

  return explicitSources.some(isExternalSourceValue) || Boolean(getExternalProfileSource(record));
}

function getRecordValue(record, fieldKey) {
  const value = getRecordRawValue(record, fieldKey);

  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return Object.values(value).join(' ');

  return value;
}

function formatAvatarDateTime(value) {
  if (!value) return '';

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const parts = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const getPart = (type) => parts.find((part) => part.type === type)?.value ?? '';
  const dateText = `${getPart('month')} ${getPart('day')},${getPart('year')}`;
  const timeText = `${getPart('hour')}:${getPart('minute')} ${getPart('dayPeriod')}`;

  return `${dateText} | ${timeText}`;
}

function getAvatarInitial(value) {
  return String(value || 'U').trim().charAt(0).toUpperCase() || 'U';
}

function getAvatarFieldValue(item, keys) {
  const key = keys.find((candidateKey) => {
    const value = getRecordRawValue(item, candidateKey);
    return value !== null && value !== undefined && value !== '';
  });

  return key ? getRecordValue(item, key) : '';
}

function normalizeAvatarItems(field, record) {
  const rawValue = getRecordRawValue(record, field.key);
  const items = Array.isArray(rawValue) ? rawValue : (rawValue ? [rawValue] : []);
  const config = field.renderConfig ?? {};
  const nameKeys = [
    config.nameField,
    config.labelField,
    'recruiterName',
    'name',
    'fullName',
    'displayName',
    'userName',
    'email',
    'recruiterEmail',
  ].filter(Boolean);
  const metaKeys = [
    config.metaField,
    config.dateField,
    config.assignedDateField,
    'assignedDateTime',
    'assignedDate',
    'createdAt',
  ].filter(Boolean);

  return items.map((item, index) => {
    const recordItem = typeof item === 'object' && item !== null ? item : { value: item };
    const name = getAvatarFieldValue(recordItem, nameKeys) || String(item || 'User');
    const metaValue = getAvatarFieldValue(recordItem, metaKeys);

    return {
      key: getAvatarFieldValue(recordItem, ['recruiterId', 'id', '_id', 'email', 'recruiterEmail']) || `${name}-${index}`,
      name,
      initial: getAvatarInitial(name),
      meta: formatAvatarDateTime(metaValue),
    };
  });
}

function AvatarTooltipContent({ item }) {
  return (
    <div className="job-avatar-tooltip">
      <div className="job-avatar-tooltip-name">{item.name}</div>
      {item.meta && <div className="job-avatar-tooltip-meta">{item.meta}</div>}
    </div>
  );
}

function AvatarMoreTooltipContent({ items }) {
  return (
    <div className="job-avatar-more-tooltip">
      {items.map((item) => (
        <AvatarTooltipContent item={item} key={item.key} />
      ))}
    </div>
  );
}

function isTrueValue(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';

  return Boolean(value);
}

function stringifyLinkPart(value) {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(',');
  if (typeof value === 'object') return Object.values(value).join(' ');

  return String(value);
}

function fillLinkTemplate(template, record) {
  if (!template) return '';

  return String(template)
    .replace(/\{([^}]+)\}/g, (_, key) => encodeURIComponent(stringifyLinkPart(getRecordRawValue(record, key))))
    .replace(/:([A-Za-z_][\w.]*)/g, (_, key) => encodeURIComponent(stringifyLinkPart(getRecordRawValue(record, key))));
}

function isUrlLike(value) {
  return /^(https?:\/\/|\/)/i.test(String(value));
}

function getLinkTarget(target, href) {
  const normalizedTarget = String(target ?? '').trim().toLowerCase();

  if (normalizedTarget === 'blank' || normalizedTarget === '_blank') return '_blank';
  if (normalizedTarget === 'self' || normalizedTarget === '_self') return '_self';
  if (normalizedTarget) return target;
  if (/^https?:\/\//i.test(String(href))) return '_blank';

  return undefined;
}

function isExternalUrl(value) {
  return /^https?:\/\//i.test(String(value));
}

function getFieldHref(field, record, value) {
  const template = field.routeUrl
    ?? field.routeURL
    ?? field.linkTemplate
    ?? field.linkUrl
    ?? field.href
    ?? field.url
    ?? field.action;

  if (template) return fillLinkTemplate(template, record);
  if (isUrlLike(value)) return String(value);

  return '';
}

function highlightText(text, query) {
  if (!query || query.length < 3 || !text || typeof text !== 'string' || text === '-') return text;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const parts = [];
  let lastIndex = 0;

  while (lastIndex <= text.length) {
    const matchIndex = lowerText.indexOf(lowerQuery, lastIndex);
    if (matchIndex === -1) {
      parts.push(text.slice(lastIndex));
      break;
    }
    if (matchIndex > lastIndex) parts.push(text.slice(lastIndex, matchIndex));
    parts.push(
      <mark
        key={matchIndex}
        className="search-highlight"
        style={{ backgroundColor: '#ffff00', color: 'inherit', padding: 0, borderRadius: 2 }}
      >
        {text.slice(matchIndex, matchIndex + query.length)}
      </mark>
    );
    lastIndex = matchIndex + query.length;
  }

  return parts;
}

function renderLinkedValue(field, record, value, searchQuery = '') {
  const href = getFieldHref(field, record, value);
  const target = getLinkTarget(field.linkTarget, href);
  const highlightedValue = highlightText(value, searchQuery);

  if (isExternalUrl(href)) {
    return (
      <Tooltip title={value !== '-' ? value : undefined}>
      <a
        className="job-cell-link job-cell-value"
        href={href}
        rel={target === '_blank' ? 'noreferrer' : undefined}
        target={target}
      >
        {highlightedValue}
      </a>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={value !== '-' ? value : undefined}>
    <RouterLink
      className="job-cell-link job-cell-value"
      target={target}
      to={href || '#'}
    >
      {highlightedValue}
    </RouterLink>
    </Tooltip>
  );
}

function formatDateValue(value) {
  if (value === null || value === undefined || value === '') return '-';

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return getRecordValue({ value }, 'value');

  const month = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    timeZone: 'UTC',
  }).format(date);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = date.getUTCFullYear();

  return `${month} ${day}, ${year}`;
}

function isDateField(field) {
  return /date/i.test(`${field.key ?? ''} ${field.label ?? ''} ${field.type ?? ''}`);
}

function formatDisplayTemplate(template, record) {
  if (typeof template !== 'string' || template.trim() === '') return '';

  const rendered = template.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, key) => {
    const value = getRecordValue(record, key.trim());
    return value === '-' ? '' : String(value);
  });

  return rendered.replace(/\s+/g, ' ').trim();
}

function formatFieldValue(field, record) {
  const rawValue = getRecordRawValue(record, field.key);
  const templatedValue = formatDisplayTemplate(field.displayTemplate, record);

  if (templatedValue) return templatedValue;

  if (isDateField(field)) return formatDateValue(rawValue);

  return getRecordValue(record, field.key);
}

function capitalizeWords(value) {
  if (value === '-' || typeof value !== 'string' || !value.trim()) return value;

  return value.replace(/\b([A-Za-z])([A-Za-z]*)\b/g, (word, first, rest) => {
    if (word.length > 1 && word === word.toUpperCase()) return word;
    return `${first.toUpperCase()}${rest.toLowerCase()}`;
  });
}

function formatExperienceRange(value) {
  if (typeof value !== 'string' || !value.includes('-')) return value;

  return value
    .replace(/(^|\s)-\s*(\d+)(?=\s*[A-Za-z])/, '$10-$2')
    .replace(/(\d+)\s*-\s*(?=[A-Za-z])/, '$1-0 ');
}

function getOrderedFields(fields) {
  const indexedFields = fields.map((field, index) => ({ field, index }));
  const hasOrder = indexedFields.some(({ field }) => field.order !== undefined && field.order !== null && field.order !== '');

  if (!hasOrder) return fields;

  return [...indexedFields]
    .sort((a, b) => {
      const firstOrder = Number(a.field.order);
      const secondOrder = Number(b.field.order);
      const firstHasOrder = Number.isFinite(firstOrder);
      const secondHasOrder = Number.isFinite(secondOrder);

      if (firstHasOrder && secondHasOrder && firstOrder !== secondOrder) return firstOrder - secondOrder;
      if (firstHasOrder !== secondHasOrder) return firstHasOrder ? -1 : 1;

      return a.index - b.index;
    })
    .map(({ field }) => field);
}

function getActionIconUrl(action) {
  return action.iconUrl ?? action.iconURL ?? action.icon ?? action.iconPath;
}

function normalizeActionButtons(actionButtons) {
  if (!Array.isArray(actionButtons)) return [];

  return actionButtons
    .map((action, index) => ({
      ...action,
      key: action.key ?? action.name ?? action.label ?? action.routeUrl ?? action.type ?? String(index),
      label: action.label ?? action.name ?? action.title ?? action.type ?? 'Action',
      iconUrl: getActionIconUrl(action),
    }))
    .filter((action) => action.iconUrl);
}

function renderActionButton(action, record) {
  const routeUrl = fillLinkTemplate(action.routeUrl ?? action.routeURL ?? action.url ?? action.href, record);
  const normalizedType = normalizeComparableText(action.type);
  const content = (
    <img
      src={action.iconUrl}
      alt={action.label}
      className="job-field-action-icon"
    />
  );
  const className = "job-field-action-button";

  if (routeUrl && (normalizedType === 'page' || routeUrl.startsWith('/'))) {
    return (
      <Tooltip key={action.key} title={action.label}>
        <RouterLink
          aria-label={action.label}
          className={className}
          to={routeUrl}
        >
          {content}
        </RouterLink>
      </Tooltip>
    );
  }

  if (routeUrl) {
    return (
      <Tooltip key={action.key} title={action.label}>
        <a
          aria-label={action.label}
          className={className}
          href={routeUrl}
          rel="noreferrer"
          target="_blank"
        >
          {content}
        </a>
      </Tooltip>
    );
  }

  return (
    <Tooltip key={action.key} title={action.label}>
      <button
        aria-label={action.label}
        className={className}
        type="button"
      >
        {content}
      </button>
    </Tooltip>
  );
}

function getSecondaryFieldKeys(field) {
  const secondaryFields = Array.isArray(field.secondaryFields)
    ? field.secondaryFields
    : [];
  const secondaryField = field.secondaryField ? [field.secondaryField] : [];

  return [...secondaryFields, ...secondaryField]
    .filter((key, index, keys) => key && keys.indexOf(key) === index);
}

function getSecondaryFieldValue(field, record) {
  const separator = field.secondarySeparator ?? ' ';
  const values = getSecondaryFieldKeys(field)
    .map((key) => getRecordValue(record, key))
    .filter((value) => value !== '-');

  return values.length ? values.join(` ${separator} `) : '';
}

function formatListText(value) {
  return capitalizeWords(formatExperienceRange(value));
}

function getBadgeValueStyle(field, value) {
  if (!Array.isArray(field.valueStyles)) return null;

  const normalizedValue = normalizeComparableText(value);

  return field.valueStyles.find((style) => (
    normalizeComparableText(style?.value) === normalizedValue
    || normalizeComparableText(style?.label) === normalizedValue
  )) ?? null;
}

function getBadgeStyle(styleConfig) {
  const color = styleConfig?.color;
  const bg = styleConfig?.bg ?? styleConfig?.background ?? styleConfig?.backgroundColor;

  return {
    ...(color ? { '--job-cell-badge-color': color } : {}),
    ...(bg ? { '--job-cell-badge-bg': bg } : {}),
  };
}

function getBadgeVariant(styleConfig) {
  return normalizeComparableText(styleConfig?.varient ?? styleConfig?.variant)
    .replace(/[^a-z0-9-]/g, '-');
}

function renderBadgeField(field, value) {
  const valueStyle = getBadgeValueStyle(field, value);
  const styleConfig = valueStyle ?? field.commonStyle ?? {};
  const label = valueStyle?.label ?? value;
  const variant = getBadgeVariant(styleConfig);
  const className = [
    'job-cell-badge',
    'job-cell-value',
    variant ? `job-cell-badge--${variant}` : '',
  ].filter(Boolean).join(' ');

  return (
    <Tooltip title={label !== '-' ? label : undefined}>
      <Tag className={className} style={getBadgeStyle(styleConfig)}>
        {label}
      </Tag>
    </Tooltip>
  );
}

function renderAvatarField(field, record) {
  const avatars = normalizeAvatarItems(field, record);
  const visibleAvatars = avatars.slice(0, 2);
  const overflowAvatars = avatars.slice(2);

  if (!avatars.length) return <Text className="job-cell-primary job-cell-value">-</Text>;

  return (
    <span className="job-avatar-group">
      {visibleAvatars.map((item, index) => (
        <Tooltip
          key={item.key}
          overlayClassName="job-avatar-tooltip-overlay"
          title={<AvatarTooltipContent item={item} />}
        >
          <Avatar className={`job-avatar job-avatar--${index + 1}`}>
            {item.initial}
          </Avatar>
        </Tooltip>
      ))}
      {overflowAvatars.length > 0 && (
        <Tooltip
          overlayClassName="job-avatar-tooltip-overlay job-avatar-more-tooltip-overlay"
          title={<AvatarMoreTooltipContent items={overflowAvatars} />}
        >
          <Avatar className="job-avatar job-avatar--more">
            +{overflowAvatars.length}
          </Avatar>
        </Tooltip>
      )}
    </span>
  );
}

function renderFieldValue(field, record, searchQuery = '') {
  if (normalizeComparableText(field.renderType) === 'avatar') return renderAvatarField(field, record);

  const value = formatListText(formatFieldValue(field, record));
  const actionButtons = normalizeActionButtons(field.actionButtons);
  const secondaryValue = formatListText(getSecondaryFieldValue(field, record));
  const isBadge = normalizeComparableText(field.renderType) === 'badge';
  const renderedValue = isBadge && value !== '-'
    ? renderBadgeField(field, value)
    : (!isTrueValue(field.isLink) || value === '-'
      ? <Tooltip title={value !== '-' ? value : undefined}><Text className="job-cell-primary job-cell-value">{highlightText(value, searchQuery)}</Text></Tooltip>
      : renderLinkedValue(field, record, value, searchQuery));

  return (
    <span className="job-cell-content">
      <span className="job-cell-main-line">
        {renderedValue}
        {actionButtons.length > 0 && (
          <span className="job-field-actions">
            {actionButtons.map((action) => renderActionButton(action, record))}
          </span>
        )}
      </span>
      {secondaryValue && (
        <Tooltip title={secondaryValue}>
        <span className="job-cell-secondary">
          {highlightText(secondaryValue, searchQuery)}
        </span>
        </Tooltip>
      )}
    </span>
  );
}

function getComparableValue(record, fieldKey) {
  const value = getRecordValue(record, fieldKey);
  return value === '-' ? '' : String(value);
}


function normalizeComparableText(value) {
  return String(value ?? '').trim().toLowerCase();
}

function getTabKey(tab, index) {
  return tab?.key ?? tab?.value ?? tab?.id ?? (index !== undefined ? String(index) : undefined);
}

function isAllTabKey(tabKey) {
  return ['all', ''].includes(normalizeComparableText(tabKey));
}

function recordMatchesTab(record, activeTab, activeTabConfig, fields, tabField) {
  if (isAllTabKey(activeTab)) return true;

  const tabValue = activeTabConfig?.filterValue
    ?? activeTabConfig?.status
    ?? activeTabConfig?.title
    ?? activeTabConfig?.tabTitle
    ?? activeTabConfig?.name
    ?? (typeof activeTabConfig?.label === 'string' ? activeTabConfig.label : undefined)
    ?? activeTabConfig?.value
    ?? activeTabConfig?.key
    ?? activeTab;
  const normalizedTabValue = normalizeComparableText(tabValue);
  const explicitField = activeTabConfig?.field
    ?? activeTabConfig?.fieldName
    ?? activeTabConfig?.filterField
    ?? activeTabConfig?.dataIndex
    ?? tabField;

  if (explicitField) {
    return normalizeComparableText(getRecordValue(record, explicitField)) === normalizedTabValue;
  }

  const statusFields = fields.filter((field) => {
    const key = normalizeComparableText(field.key);
    const label = normalizeComparableText(field.label);
    return key.includes('status') || label.includes('status');
  });
  const candidateFields = statusFields.length ? statusFields : fields;

  return candidateFields.some((field) => (
    normalizeComparableText(getRecordValue(record, field.key)) === normalizedTabValue
  ));
}

function getRowKey(record, index, rowKey) {
  if (typeof rowKey === 'function') return rowKey(record, index);
  if (typeof rowKey === 'string') return record?.[rowKey];

  return record.id ?? record._id ?? record.key ?? index;
}

function getSortDirection(order) {
  if (order === 'ascend') return 'asc';
  if (order === 'descend') return 'desc';
  return '';
}

function getActionLabel(action) {
  return action.label ?? action.name ?? action.title ?? action.value;
}

function getActionKey(action, index) {
  return action.key ?? action.value ?? action.route ?? action.name ?? String(index);
}

const ACTION_ICON_MAP = {
  delete: <DeleteOutlined />,
  edit: <EditOutlined />,
  mail: <MailOutlined />,
  shareAlt: <ShareAltOutlined />,
  swap: <SwapOutlined />,
  thunderbolt: <ThunderboltOutlined />,
  userAdd: <UserAddOutlined />,
  userSwitch: <UserSwitchOutlined />,
};

// antd's Dropdown `menu.items` only understands {key, label, icon, danger, ...}.
// Admin-configured actions carry extra config fields (actionType, routeTemplate,
// popupKey, popupState, ...) that must NOT be spread onto the menu item, or antd
// forwards them straight onto the underlying DOM node.
function toMenuItem(action) {
  return {
    key: action.key,
    label: action.label,
    icon: ACTION_ICON_MAP[action.icon] ?? undefined,
    danger: action.danger || undefined,
  };
}

function normalizeActions(actions) {
  if (!Array.isArray(actions)) return [];

  return actions
    .map((action, index) => {
      const label = getActionLabel(action);
      const key = getActionKey(action, index);

      if (!label || !key) return null;

      return {
        ...action,
        key: String(key),
        label,
        route: action.route || action.routeTemplate || (
          action.actionType === 'modal' && (action.popupKey || key)
            ? `/popup/${action.popupKey || key}`
            : ''
        ),
      };
    })
    .filter(Boolean);
}

function isActionValueAllowed(value) {
  if (value === undefined || value === null) return true;
  if (typeof value === 'object') {
    return isActionValueAllowed(value.permission ?? value.allowed ?? value.value);
  }
  return value !== false && value !== 0 && value !== '0';
}

function selectedRecordsAllowAction(action, records) {
  if (!action.permissionKey || !records.length) return true;
  return records.every((record) => isActionValueAllowed(
    getNestedRecordValue(record, `actionPermission.${action.permissionKey}`),
  ));
}

function roleAllowsAction(permission, moduleName) {
  if (!permission) return true;
  try {
    const permissions = JSON.parse(localStorage.getItem('menuPermission') || '{}');
    const [configuredModule, configuredKey] = String(permission).split('.');
    const moduleKey = configuredModule || String(moduleName || '').replace(/s$/i, '');
    const modulePermissions = permissions[moduleKey]
      ?? permissions[String(moduleName || '')]
      ?? permissions[String(moduleName || '').replace(/s$/i, '')]
      ?? {};
    return isActionValueAllowed(modulePermissions[configuredKey]);
  } catch {
    return true;
  }
}

function isEmptyRuleValue(value) {
  return value === undefined || value === null || value === ''
    || (Array.isArray(value) && value.length === 0);
}

function conditionMatches(condition, record, scope) {
  const operator = condition?.operator;
  const actual = operator === 'scope' ? scope : getNestedRecordValue(record, condition?.field);
  const expected = condition?.value;
  const normalizedActual = normalizeComparableText(actual);
  const normalizedExpected = normalizeComparableText(expected);

  if (operator === 'eq' || operator === 'scope') return normalizedActual === normalizedExpected;
  if (operator === 'ne') return normalizedActual !== normalizedExpected;
  if (operator === 'isEmpty') return isEmptyRuleValue(actual);
  if (operator === 'notEmpty') return !isEmptyRuleValue(actual);
  if (operator === 'fieldEq') {
    return normalizedActual === normalizeComparableText(getNestedRecordValue(record, condition?.compareField));
  }
  if (operator === 'contains') {
    if (Array.isArray(actual)) return actual.some((item) => normalizeComparableText(item).includes(normalizedExpected));
    return normalizedActual.includes(normalizedExpected);
  }
  return false;
}

function isActionBlocked(actionKey, records, rules, scope) {
  if (!records.length || !rules.length) return false;
  return records.some((record) => rules.some((rule) => (
    rule?.isActive !== false
    && (rule?.blockedActions ?? []).map(String).includes(String(actionKey))
    && (rule?.conditions ?? []).every((condition) => conditionMatches(condition, record, scope))
  )));
}

function getActiveSorter(sorter) {
  if (Array.isArray(sorter)) {
    return sorter.find((item) => item?.order) ?? {};
  }

  return sorter ?? {};
}

function getDefaultListSnapshotKey({ moduleName, activeTabScope, pageSize, sort, sortDir }) {
  return [
    moduleName ?? '',
    activeTabScope ?? '',
    pageSize,
    sort ?? '',
    sortDir ?? '',
  ].join('|');
}

function getPositivePage(value) {
  const page = Number(value);

  return Number.isInteger(page) && page > 0 ? page : 1;
}

function getPageFromSearchParams(searchParams) {
  for (const key of searchParams.keys()) {
    const match = /^p(\d+)$/.exec(key);

    if (match) return getPositivePage(match[1]);
  }

  return 1;
}

function removePageSearchParams(searchParams) {
  [...searchParams.keys()].forEach((key) => {
    if (/^p\d+$/.test(key) || key === 'page') searchParams.delete(key);
  });
}

const EMPTY_FIELDS = [];

// RecordCard renders using the exact same field.render / renderFieldValue functions
// that the list view table uses — no hardcoded field slots except name (header) and
// action (footer). Every other visible field is a dynamic label + value row.
function RecordCard({ record, fields, selected, onSelect, onPreview, isControlled }) {
  const nameField = fields.find((f) => f.cardRole === 'name');
  const actionField = fields.find((f) => f.cardRole === 'action');
  const headerActionField = fields.find((f) => f.cardRole === 'headerAction');
  const bodyFields = fields.filter((f) => f !== nameField && f !== actionField && f !== headerActionField);

  const nameValue = nameField ? getRecordValue(record, getFieldValue(nameField)) : '-';

  const actionNode = actionField?.render
    ? actionField.render(getRecordRawValue(record, getFieldValue(actionField)), record)
    : null;

  const headerActionNode = headerActionField?.render
    ? headerActionField.render(getRecordRawValue(record, getFieldValue(headerActionField)), record)
    : null;

  return (
    <div className={`param-card${selected ? ' param-card--selected' : ''}`}>

      {/* Header — name always shown prominently; skips field.render (table-specific) */}
      <div className="param-card__header">
        <div className="param-card__name-row">
          <Checkbox
            checked={selected}
            onChange={onSelect}
            onClick={(e) => e.stopPropagation()}
            className="param-card__checkbox"
          />
          {onPreview && (
            <button
              type="button"
              className="job-row-eye-button"
              aria-label="Preview"
              onClick={onPreview}
            >
              <img src={eyeOutlinedIcon} alt="" className="job-row-eye-icon" />
            </button>
          )}
          
          <Text className="param-card__name">{nameValue}</Text>
          {headerActionNode && (
            <div className="param-card__header-actions">{headerActionNode}</div>
          )}
        </div>
      </div>

      {/* Body — every other visible field rendered dynamically, same as list columns */}
      {bodyFields.length > 0 && (
        <div className="param-card__body-fields">
          {bodyFields.map((field) => {
            const fieldKey = getFieldValue(field);
            const rawValue = getRecordRawValue(record, fieldKey);
            const displayNode = isControlled && typeof field.render === 'function'
              ? field.render(rawValue, record)
              : renderFieldValue(field, record);
            return (
              <div key={fieldKey} className="param-card__field-row">
                <Text className="param-card__field-label">{getFieldLabel(field)}</Text>
                <div className="param-card__field-value">{displayNode}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Divider */}
      <div className="param-card__divider" />

      {/* Footer — action field only */}
      <div className="param-card__footer">
        <div className="param-card__footer-left" />
        <div className="param-card__footer-right">{actionNode}</div>
      </div>
    </div>
  );
}

function hasFilterValue(row) {
  if (row.operator === 'isEmpty' || row.operator === 'notEmpty') return true;
  if (row.value) return true;
  if (row.from || row.to) return true;
  if (Array.isArray(row.values) && row.values.length > 0) return true;
  return false;
}

function formatChipFieldLabel(row) {
  if (row.fieldLabel) return row.fieldLabel;
  return (row.field ?? '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^(.)/, (s) => s.toUpperCase())
    .trim();
}

function formatChipValue(row) {
  if (row.operator === 'isEmpty') return 'Is Empty';
  if (row.operator === 'notEmpty') return 'Not Empty';
  if (row.value) return `"${row.value}"`;
  if (row.from && row.to) return `${row.from} to ${row.to}`;
  if (row.from) return `from ${row.from}`;
  if (row.to) return `to ${row.to}`;
  if (Array.isArray(row.values) && row.values.length > 0) {
    const shown = row.values.slice(0, 2).join(', ');
    return row.values.length > 2 ? `${shown} +${row.values.length - 2}` : shown;
  }
  return '';
}

function FilterChipsBar({ filters, onRemove, onClearAll }) {
  const chips = (filters ?? [])
    .map((row, originalIdx) => ({ row, originalIdx }))
    .filter(({ row }) => row.field && hasFilterValue(row));

  if (chips.length === 0) return null;

  return (
    <Flex gap={6} wrap="wrap" align="center" className="filter-chips-bar">
      <Text className="filter-chips-bar__label">Filters:</Text>
      {chips.map(({ row, originalIdx }) => (
        <Tag
          key={`${row.field}-${originalIdx}`}
          closable
          onClose={() => onRemove(originalIdx)}
          className="filter-chip"
        >
          <span className="filter-chip__field">{formatChipFieldLabel(row)}</span>
          <span className="filter-chip__sep">: </span>
          <span className="filter-chip__value">{formatChipValue(row)}</span>
        </Tag>
      ))}
      {chips.length > 1 && (
        <Button type="link" size="small" onClick={onClearAll} className="filter-chips-clear">
          Clear all
        </Button>
      )}
    </Flex>
  );
}

export default function ListView({
  moduleName,
  actions = [],
  breadcrumbItems = [],
  dataSource = [],
  className = '',
  initialPageSize = 10,
  onAction,
  onSummaryClick,
  rowKey,
  showSummaryButton = false,
  summaryButtonLabel = 'View Summary',
  summaryRoute,
  tabs,
  tableClassName = 'job-list-table',
  // new controlled-mode props
  fields: externalFields = EMPTY_FIELDS,
  listName = '',
  loading: propLoading = false,
  total: externalTotal,
  current: externalCurrent,
  pageSize: externalPageSize,
  onPageChange,
  onPageSizeChange,
  onSearch,
  showSearch = true,
  showViewToggle = false,
  activeView = 'list',
  onViewChange,
  createAction,
  // Source tab props
  selectedSource,
  onTabChange,
  fetchData = getModuleDataList,
  responseMapper = mapListResponse,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { canView, canAdd, canEdit, canDelete } = useMenuPermission(moduleName);
  const [searchParams] = useSearchParams();
  const routePage = getPageFromSearchParams(searchParams);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [appliedFilterRows, setAppliedFilterRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pagination, setPagination] = useState({ current: routePage, pageSize: initialPageSize });
  const [sortConfig, setSortConfig] = useState({ sort: '', sortDir: '' });
  const [visibleColumnKeys, setVisibleColumnKeys] = useState([]);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const [selectedCardIds, setSelectedCardIds] = useState([]);
  const [moduleRows, setModuleRows] = useState(dataSource);
  const [moduleTotal, setModuleTotal] = useState(dataSource.length);
  const [moduleTabs, setModuleTabs] = useState([]);
  const [moduleFields, setModuleFields] = useState([]);
  const [moduleTabField, setModuleTabField] = useState('');
  const [moduleActions, setModuleActions] = useState([]);
  const [moduleActionRules, setModuleActionRules] = useState([]);
  const [moduleLoading, setModuleLoading] = useState(false);
  const [moduleError, setModuleError] = useState(null);
  const [defaultListSnapshot, setDefaultListSnapshot] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const isControlled = Boolean(onPageChange || onPageSizeChange || onSearch);

  // Controlled mode overrides - must be declared before useFieldConfig hook
  const effectiveRows = isControlled ? dataSource : moduleRows;
  const effectiveTotal = isControlled ? (externalTotal ?? 0) : moduleTotal;
  const effectiveFields = isControlled ? externalFields : moduleFields;
  const effectiveTabs = isControlled ? tabs : moduleTabs;
  const effectiveTabField = isControlled ? '' : moduleTabField;
  const effectiveError = isControlled ? null : moduleError;
  const hasExternalSourceRows = useMemo(
    () => effectiveRows.some((record) => isExternalSourceRecord(record)),
    [effectiveRows],
  );
  const hasExternalSourceFields = useMemo(
    () => effectiveFields.some((field) => isExternalSourceField(field)),
    [effectiveFields],
  );
  const resolvedActiveView = (hasExternalSourceRows || hasExternalSourceFields) ? 'card' : activeView;
  const configuredActions = actions.length > 0 ? actions : moduleActions;
  const actionItems = useMemo(() => normalizeActions(configuredActions), [configuredActions]);

  const actionByKey = useMemo(() => (
    new Map(actionItems.map((action) => [action.key, action]))
  ), [actionItems]);
  const visibleActionItems = useMemo(() => (
    actionItems.filter((action) => {
      // Infer permission key: explicit > danger convention > key name
      const pk = action.permissionKey ?? (action.danger ? 'delete' : action.key === 'edit' ? 'edit' : null);
      if (pk === 'edit' && !canEdit) return false;
      if (pk === 'delete' && !canDelete) return false;
      if (!roleAllowsAction(action.permission, moduleName)) return false;
      if (action.showInList === false) return false;
      if (!action.allowedTabs) return true;
      return action.allowedTabs.some((tab) => normalizeComparableText(tab) === normalizeComparableText(activeTab));
    })
  ), [actionItems, activeTab, canEdit, canDelete, moduleName]);
  const selectedActionItems = useMemo(() => visibleActionItems.filter((action) => (
    (selectedRowKeys.length === 1 || action.allowMultiple || action.danger)
    && selectedRecordsAllowAction(action, selectedRows)
    && !isActionBlocked(action.key, selectedRows, moduleActionRules, activeTab)
  )), [activeTab, moduleActionRules, selectedRowKeys.length, selectedRows, visibleActionItems]);

  const hasModuleName = typeof moduleName === 'string'
    ? moduleName.trim().length > 0
    : Boolean(moduleName);
  const createRoute = hasModuleName
    ? `/${String(moduleName).trim()}-create${createAction ? `?action=${encodeURIComponent(createAction)}` : ''}`
    : '';
  useEffect(() => {
    if (hasModuleName && !canView) {
      navigate('/', { replace: true });
    }
  }, [canView, hasModuleName, navigate]);

  const updatePageRoute = (page, { replace = false } = {}) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    removePageSearchParams(nextSearchParams);

    const baseSearch = nextSearchParams.toString();
    const pageSearch = page > 1 ? `p${page}` : '';
    const nextSearch = [baseSearch, pageSearch].filter(Boolean).join('&');
    const currentSearch = location.search.replace(/^\?/, '');

    if (nextSearch !== currentSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace },
      );
    }
  };

  const setPaginationPage = (page, options) => {
    setPagination((current) => ({ ...current, current: page }));
    updatePageRoute(page, options);
  };

  const resetPaginationPage = () => {
    setPaginationPage(1, { replace: true });
  };

  const { config, loading: configLoading, error } = useFieldConfig(!isControlled && hasModuleName ? moduleName : null);

  useEffect(() => {
    setPagination((current) => (
      current.current === routePage ? current : { ...current, current: routePage }
    ));
  }, [routePage]);

  useEffect(() => {
    if (!isControlled) return;

    setPagination((current) => {
      const nextCurrent = getPositivePage(externalCurrent ?? current.current);
      const nextPageSize = Number(externalPageSize) > 0 ? Number(externalPageSize) : current.pageSize;

      return current.current === nextCurrent && current.pageSize === nextPageSize
        ? current
        : { current: nextCurrent, pageSize: nextPageSize };
    });
  }, [externalCurrent, externalPageSize, isControlled]);

  const refreshTimestamp = location.state?.refreshTimestamp;
  useEffect(() => {
    if (!refreshTimestamp) return;
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setRefreshKey((k) => k + 1);
  }, [refreshTimestamp]);

  const activeTabConfig = useMemo(() => {
    const availableTabs = [
      ...(Array.isArray(tabs) ? tabs : []),
      ...moduleTabs,
    ];

    return availableTabs.find((tab, index) => String(getTabKey(tab, index)) === activeTab);
  }, [activeTab, moduleTabs, tabs]);
  const activeTabField = activeTabConfig?.field
    ?? activeTabConfig?.fieldName
    ?? activeTabConfig?.filterField
    ?? activeTabConfig?.dataIndex
    ?? moduleTabField;
  const activeTabScope = isAllTabKey(activeTab) ? '' : activeTab;
  const canFilterActiveTab = !activeTabScope
    && !isAllTabKey(activeTab)
    && normalizeComparableText(activeTabField).length > 0;
  const hasActiveSearch = committedSearch.trim().length >= 3;
  const hasActiveFieldFilters = appliedFilterRows.some((row) => row.field);
  const defaultListSnapshotKey = getDefaultListSnapshotKey({
    moduleName,
    activeTabScope,
    pageSize: pagination.pageSize,
    sort: sortConfig.sort,
    sortDir: sortConfig.sortDir,
  });
  const hasUsableDefaultSnapshot = defaultListSnapshot?.key === defaultListSnapshotKey;

  useEffect(() => {
    if (isControlled) return undefined;

    if (!hasModuleName) {
      setModuleRows([]);
      setModuleTotal(0);
      setModuleTabs([]);
      setModuleFields([]);
      setModuleTabField('');
      setModuleActions([]);
      setModuleActionRules([]);
      setModuleError(null);
      return undefined;
    }

    let cancelled = false;
    // Fetch all rows only when a local search is active so it can match across
    // the full result set. Field filters are handled server-side, so normal
    // pagination applies even while they're active.
    const shouldFetchAllRows = hasActiveSearch && moduleTotal > pagination.pageSize;
    const limit = shouldFetchAllRows ? moduleTotal : pagination.pageSize;
    const offset = shouldFetchAllRows ? 0 : (pagination.current - 1) * pagination.pageSize;

    setModuleLoading(!(!hasActiveSearch && !hasActiveFieldFilters && pagination.current === 1 && hasUsableDefaultSnapshot));
    setModuleError(null);

    fetchData(moduleName, limit, offset, {
      scope: activeTabScope,
      sort: sortConfig.sort,
      sortDir: sortConfig.sortDir,
      filters: appliedFilterRows,
    })
      .then((response) => {
        if (cancelled) return;
        const {
          rows,
          total,
          tabs: responseTabs,
          fields: responseFields,
          tabField,
          actions: responseActions,
          actionRules: responseActionRules,
        } = responseMapper(response, moduleName);
        setModuleRows(rows);
        setModuleTotal(total);
        setModuleTabs(responseTabs);
        setModuleFields(responseFields);
        setModuleTabField(tabField);
        setModuleActions(responseActions);
        setModuleActionRules(responseActionRules);

        if (!hasActiveSearch && !hasActiveFieldFilters && pagination.current === 1) {
          setDefaultListSnapshot({
            key: defaultListSnapshotKey,
            rows,
            total,
            tabs: responseTabs,
            fields: responseFields,
            tabField,
            actions: responseActions,
            actionRules: responseActionRules,
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setModuleRows([]);
        setModuleTotal(0);
        setModuleTabs([]);
        setModuleFields([]);
        setModuleTabField('');
        setModuleActions([]);
        setModuleActionRules([]);
        setModuleError(err.message);
        if (hasModuleName) {
          message.error('Invalid module name or data could not be loaded.');
        }
      })
      .finally(() => {
        if (!cancelled) setModuleLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTabScope, appliedFilterRows, defaultListSnapshotKey, fetchData, hasActiveFieldFilters, hasActiveSearch, hasModuleName, hasUsableDefaultSnapshot, moduleName, moduleTotal, pagination.current, pagination.pageSize, refreshKey, responseMapper, sortConfig.sort, sortConfig.sortDir]);

  const normalizedFields = useMemo(() => {
    const usesExternalFields = isControlled && Array.isArray(externalFields) && externalFields.length;
    const usesModuleFields = !isControlled && Array.isArray(moduleFields) && moduleFields.length;
    const usesConfigFields = !error && Array.isArray(config) && config.length;

    const fieldSource = usesExternalFields
      ? externalFields
      : usesModuleFields
        ? moduleFields
        : (usesConfigFields ? config : []);

    const moduleFieldByKey = new Map(
      moduleFields
        .map((field) => [getFieldValue(field), field])
        .filter(([key]) => key)
    );

    const normalized = fieldSource
      .map((field) => {
        const key = getFieldValue(field);
        const moduleField = moduleFieldByKey.get(key) ?? {};

        return {
          ...moduleField,
          ...field,
          key,
          label: getFieldLabel(field) ?? getFieldLabel(moduleField),
        };
      })
      .filter((field) => field.key && field.label);

    return getOrderedFields(normalized);
  }, [config, error, externalFields, isControlled, moduleFields]);

  const tableFields = useMemo(() => (
    normalizedFields
      .filter((field) => {
        if (typeof field.isVisible === 'boolean') return field.isVisible;
        if (typeof field.isVisible === 'string') return field.isVisible.toLowerCase() === 'true';
        return true;
      })
      .map((field) => ({
        key: field.key,
        label: field.label,
      }))
  ), [normalizedFields]);

  const tableFieldKeys = tableFields.map((field) => field.key);
  const visibleTableColumnKeys = visibleColumnKeys.filter((key) => tableFieldKeys.includes(key));
  const visibleKeys = visibleTableColumnKeys.length
    ? visibleTableColumnKeys
    : tableFields.slice(0, 6).map((field) => field.key);
  const minimumVisibleColumns = Math.min(6, tableFields.length);
  const hasHorizontalScroll = visibleKeys.length > 6;

  // Card view: tableHidden fields always show (they exist for cards only);
  // all other fields respect the same visibility as list columns.
  const visibleCardFields = useMemo(() => (
    normalizedFields.filter((field) => !field.cardHidden && (field.tableHidden || visibleKeys.includes(field.key)))
  ), [normalizedFields, visibleKeys]);

  const filteredData = useMemo(() => {
    const q = search.toLowerCase();
    const sourceData = effectiveRows;
    const tabbedData = canFilterActiveTab
      ? sourceData.filter((record) => (
        recordMatchesTab(record, activeTab, activeTabConfig, normalizedFields, activeTabField)
      ))
      : sourceData;

    const searchedData = !q || q.length < 3
      ? tabbedData
      : tabbedData.filter((record) => (
        normalizedFields.some((field) => (
          String(getRecordValue(record, field.key)).toLowerCase().includes(q)
        ))
      ));

    // Field filters are evaluated server-side (sent via the API call); only
    // the local text search needs client-side narrowing here.
    return searchedData;
  }, [activeTab, activeTabConfig, activeTabField, canFilterActiveTab, effectiveRows, normalizedFields, search]);

  const pagedFilteredData = useMemo(() => {
    if (!hasActiveSearch) return filteredData;

    const start = (pagination.current - 1) * pagination.pageSize;
    return filteredData.slice(start, start + pagination.pageSize);
  }, [filteredData, hasActiveSearch, pagination.current, pagination.pageSize]);

  // When searching locally, use the client-filtered count. Otherwise the API
  // already returns the correct total (accounting for server-side filters).
  const tableTotal = hasActiveSearch ? filteredData.length : effectiveTotal;
  const showSearchResultsCount = committedSearch.trim().length >= 3;
  const searchResultsCount = String(filteredData.length).padStart(2, '0');
  const hasSourceRows = effectiveTotal > 0 || effectiveRows.length > 0;
  const emptyMessage = !hasModuleName
    ? 'Please provide a module name to view data.'
    : moduleError
      ? 'Invalid module name or data could not be loaded.'
      : error
        ? 'Field configuration failed to load.'
        : 'No data found.';
  const listTabs = useMemo(() => {
    const providedTabs = Array.isArray(tabs) ? tabs : [];

    if (providedTabs.length) return providedTabs;
    if (moduleTabs.length) return moduleTabs;

    return [{ key: 'all', title: moduleName ?? 'List', count: tableTotal }];
  }, [moduleName, moduleTabs, tableTotal, tabs]);

  useEffect(() => {
    if (!listTabs.length) return;

    const activeTabExists = listTabs.some((tab) => String(tab?.key ?? tab?.value ?? tab?.id) === activeTab);

    if (!activeTabExists) {
      const firstTabKey = listTabs[0]?.key ?? listTabs[0]?.value ?? listTabs[0]?.id;
      if (firstTabKey) setActiveTab(String(firstTabKey));
    }
  }, [activeTab, listTabs]);

  useEffect(() => {
    if (!isControlled || !selectedSource || !listTabs.length) return;

    const matchingTab = listTabs.find((tab, index) => (
      normalizeComparableText(getTabKey(tab, index)) === normalizeComparableText(selectedSource)
    ));
    const nextActiveTab = matchingTab ? String(getTabKey(matchingTab)) : '';

    if (nextActiveTab && nextActiveTab !== activeTab) {
      setActiveTab(nextActiveTab);
    }
  }, [activeTab, isControlled, listTabs, selectedSource]);

  const handlePreviewOpen = (record) => {
    navigate('/popup/preview', {
      state: {
        background: location,
        moduleName,
        record,
        fields: normalizedFields,
      },
    });
  };

  const columns = useMemo(() => {
    const iconColumn = {
        dataIndex: 'icons',
        key: 'icons',
        visibilityKey: 'jobsGroup',
        fixed: 'left',
        width: 58,
      render: (_, record) => (
        <Space size={4} className="job-row-icons">
          <button
            type="button"
            className="job-row-eye-button"
            aria-label="Preview job"
            onClick={() => handlePreviewOpen(record)}
          >
            <img src={eyeOutlinedIcon} alt="" className="job-row-eye-icon" />
          </button>
          
          {record.hasLinkedIn && (
            <Tooltip title="LinkedIn">
              <LinkedinFilled className="job-row-linkedin-icon" />
            </Tooltip>
          )}
        </Space>
      ),
    };

    return [
      iconColumn,
      ...normalizedFields
        .filter((field) => visibleKeys.includes(field.key))
        .map((field, idx) => ({
        title: field.label,
        dataIndex: field.key,
        key: field.key,
        fixed: idx === 0 ? 'left' : undefined,
        sorter: hasSourceRows,
        sortOrder: sortConfig.sort === field.key
          ? (sortConfig.sortDir === 'asc' ? 'ascend' : 'descend')
          : null,
        ellipsis: { showTitle: false },
        width: idx === 0 ? 220 : (hasHorizontalScroll ? 180 : Math.max(140, Math.floor(900 / Math.max(visibleKeys.length || 1, 1)))),
        render: (_, record) => renderFieldValue(field, record, committedSearch),
      })),
    ];
  }, [committedSearch, hasHorizontalScroll, hasSourceRows, location, moduleName, navigate, normalizedFields, sortConfig.sort, sortConfig.sortDir, visibleKeys]);

  const handleTableChange = (_, __, sorter) => {
    const activeSorter = getActiveSorter(sorter);
    const sort = activeSorter.field ?? activeSorter.columnKey ?? '';
    const sortDir = getSortDirection(activeSorter.order);

    if (isControlled && typeof onSearch === 'function') {
      onSearch(sortDir ? String(sort) : '', sortDir);
      return;
    }

    setSortConfig({
      sort: sortDir ? String(sort) : '',
      sortDir,
    });
    resetPaginationPage();
  };

  const handleTabChangeInternal = useCallback((key) => {
    setActiveTab(key);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    resetPaginationPage();
    
    // Call external onTabChange if provided (for controlled mode)
    if (isControlled && typeof onTabChange === 'function') {
      const selectedTab = listTabs.find((tab, index) => String(getTabKey(tab, index)) === key);
      const newSource = selectedTab?.key ?? selectedTab?.value ?? selectedTab?.id ?? key;
      onTabChange(newSource);
    }
  }, [isControlled, onTabChange, listTabs]);

  const handleActionClick = ({ key }) => {
    const action = actionByKey.get(String(key));

    if (!action) return;

    if (typeof onAction === 'function') {
      onAction({ key: action.key, label: action.label, selectedIds: selectedRowKeys, selectedRecords: selectedRows });
    }

    if (typeof action.onClick === 'function') {
      action.onClick({ action, selectedIds: selectedRowKeys, selectedRecords: selectedRows });
      return;
    }
    if (action.actionType === 'copy') {
      const values = selectedRows
        .map((record) => getNestedRecordValue(record, action.copyField || 'id') ?? record?._id)
        .filter((value) => value !== undefined && value !== null);
      navigator.clipboard.writeText(values.join('\n'))
        .then(() => message.success('Copied to clipboard'))
        .catch(() => message.error('Could not copy to clipboard'));
      return;
    }
    if (action.actionType === 'api' && action.apiPath) {
      const firstRecord = selectedRows[0] ?? {};
      const apiPath = fillLinkTemplate(action.apiPath, firstRecord);
      fetchJsonWithAuth(AUTH_URL, apiPath.startsWith('/') ? apiPath : `/${apiPath}`, {
        method: action.apiMethod || 'POST',
        body: ['GET', 'DELETE'].includes(String(action.apiMethod).toUpperCase())
          ? undefined
          : JSON.stringify({ ids: selectedRowKeys, records: selectedRows }),
      })
        .then(() => {
          message.success(`${action.label} completed`);
          setRefreshKey((value) => value + 1);
        })
        .catch((error) => message.error(error.message || `${action.label} failed`));
      return;
    }
    if (action.route) {
      const firstRecord = selectedRows[0] ?? {};
      const resolvedRoute = fillLinkTemplate(action.route, firstRecord);
      const normalizedRoute = resolvedRoute.startsWith('/') ? resolvedRoute : `/${resolvedRoute}`;

      if (action.route.startsWith('/popup/') || action.route.startsWith('popup/')) {
        navigate(normalizedRoute, {
          state: {
            background: location,
            selectedIds: selectedRowKeys,
            selectedRecords: selectedRows,
            moduleName,
            popupContext: action.popupState ?? {},
            popupAction: {
              key: action.key,
              apiMethod: action.apiMethod,
              apiPath: action.apiPath,
            },
            ...action.popupState,
          },
        });
        return;
      }
      navigate(normalizedRoute);
    }
  };

  const allColumnsVisible = visibleKeys.length === tableFields.length;
  const someColumnsVisible = visibleKeys.length > 0 && !allColumnsVisible;

  const toggleColumn = (key, checked) => {
    setVisibleColumnKeys((current) => {
      const defaultKeys = tableFields.slice(0, 6).map((field) => field.key);
      const currentKeys = current.length ? current : defaultKeys;

      if (checked) return currentKeys.includes(key) ? currentKeys : [...currentKeys, key];
      if (currentKeys.length <= minimumVisibleColumns) return currentKeys;

      return currentKeys.filter((columnKey) => columnKey !== key);
    });
  };

  const handleSummaryClick = () => {
    if (typeof onSummaryClick === 'function') {
      onSummaryClick();
      return;
    }
    if (summaryRoute) {
      navigate(summaryRoute);
      return;
    }
    setSummaryOpen(true);
  };

  useEffect(() => {
    if (!summaryOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setSummaryOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [summaryOpen]);

  const restoreDefaultListSnapshot = () => {
    if (hasUsableDefaultSnapshot) {
      setModuleRows(defaultListSnapshot.rows);
      setModuleTotal(defaultListSnapshot.total);
      setModuleTabs(defaultListSnapshot.tabs);
      setModuleFields(defaultListSnapshot.fields);
      setModuleTabField(defaultListSnapshot.tabField);
      setModuleActions(defaultListSnapshot.actions ?? []);
      setModuleActionRules(defaultListSnapshot.actionRules ?? []);
      setModuleError(null);
      setModuleLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    const nextSearch = event.target.value;
    const searchWillBeInactive = nextSearch.trim().length < 3;
    const hasAppliedFieldFilters = appliedFilterRows.some((row) => row.field);

    if (searchWillBeInactive) {
      setCommittedSearch('');
      if (!hasAppliedFieldFilters) {
        restoreDefaultListSnapshot();
      }
    }

    setSearch(nextSearch);
    resetPaginationPage();
  };

  const handleSearchEnter = () => {
    const q = search.trim();
    if (q.length >= 3) {
      setCommittedSearch(q);
      resetPaginationPage();
    }
  };

  const handleFilterReset = () => {
    restoreDefaultListSnapshot();
    setAppliedFilterRows([]);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    resetPaginationPage();
  };

  // Clear card selection when the user navigates to a different page or tab
  // so the Actions dropdown never carries stale records from the previous page.
  useEffect(() => {
    if (resolvedActiveView === 'card') {
      setSelectedCardIds([]);
      setSelectedRowKeys([]);
      setSelectedRows([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, activeTab, resolvedActiveView]);

  // --------------------------------------------------------------------------
  // 4g. NEW — Card View Handlers
  //     handleCardSelect: toggles a card's checked state and mirrors it into
  //     selectedRowKeys / selectedRows so the shared Actions dropdown fires
  //     identically in both List and Card views.
  // --------------------------------------------------------------------------
  const handleCardSelect = (key) => {
    setSelectedCardIds((prev) => {
      const next = prev.includes(key)
        ? prev.filter((id) => id !== key)
        : [...prev, key];
      const nextRows = pagedFilteredData.filter((record, idx) =>
        next.includes(String(getRowKey(record, idx, rowKey)))
      );
      setSelectedRowKeys(next);
      setSelectedRows(nextRows);
      return next;
    });
  };

  const columnVisibilityContent = (
    <div className="column-visibility-menu" onClick={(event) => event.stopPropagation()}>
      <Checkbox
        checked={allColumnsVisible}
        indeterminate={someColumnsVisible}
        onChange={(event) => {
          setVisibleColumnKeys(
            event.target.checked
              ? tableFields.map((field) => field.key)
              : tableFields.slice(0, 6).map((field) => field.key)
          );
        }}
      >
        Select All
      </Checkbox>
      {tableFields.map((field) => (
        <Checkbox
          key={field.key}
          checked={visibleKeys.includes(field.key)}
          disabled={visibleKeys.includes(field.key) && visibleKeys.length <= minimumVisibleColumns}
          onChange={(event) => toggleColumn(field.key, event.target.checked)}
        >
          {field.label}
        </Checkbox>
      ))}
    </div>
  );
  return (
    <div className={`antd dynamic-list-view ${className}`.trim()}>
      {breadcrumbItems.length > 0 && (
        <div className="job-list-header">
          <AppBreadcrumb items={breadcrumbItems} />
        </div>
      )}
      <Card className="job-tabs-card">
        <Flex align="center" justify="space-between">
          <DynamicTabs
            activeKey={activeTab}
            tabs={listTabs}
            onChange={handleTabChangeInternal}
          />
          <Flex align="center" gap={8}>
            {hasSourceRows && (
              <Input
                prefix={<SearchOutlined className="job-search-icon" />}
                placeholder="Min 3 Chars, press Enter to search"
                value={search}
                onChange={handleSearchChange}
                onPressEnter={handleSearchEnter}
                allowClear
                className="job-search-input"
              />
            )}
            {!isControlled && canAdd && (
              <Tooltip title="Add">
                <Button
                  aria-label="Add"
                  className="job-toolbar-icon-button"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    if (createRoute) navigate(createRoute);
                  }}
                />
              </Tooltip>
            )}
            <Tooltip title="Filter">
              <Badge
                count={appliedFilterRows.filter((r) => r.field && hasFilterValue(r)).length}
                size="small"
                offset={[-2, 2]}
              >
                <Button
                  className="job-toolbar-icon-button"
                  icon={<FilterOutlined />}
                  onClick={() => setFiltersOpen(true)}
                />
              </Badge>
            </Tooltip>
            
            {showViewToggle &&(
              <Flex align="center" gap={6}>
                <Tooltip title="List View">
                  <Button
                    className={`job-toolbar-icon-button param-view-toggle-btn${resolvedActiveView === 'list' ? ' param-view-toggle-btn--active' : ''}`}
                    icon={<UnorderedListOutlined />}
                    onClick={() => { onViewChange?.('list'); setSelectedCardIds([]); setSelectedRowKeys([]); setSelectedRows([]); }}
                  />
                </Tooltip>
                <Tooltip title="Card View">
                  <Button
                    className={`job-toolbar-icon-button param-view-toggle-btn${resolvedActiveView === 'card' ? ' param-view-toggle-btn--active' : ''}`}
                    icon={<AppstoreOutlined />}
                    onClick={() => { onViewChange?.('card'); setSelectedCardIds([]); setSelectedRowKeys([]); setSelectedRows([]); }}
                  />
                </Tooltip>
              </Flex>
            )}
            
            {showSummaryButton && (
              <Button
                className="job-summary-button"
                type="primary"
                onClick={handleSummaryClick}
              >
                {summaryButtonLabel}
              </Button>
            )}
          </Flex>
        </Flex>
      </Card>
      {hasSourceRows && (
        <>
        <Card className="job-toolbar-card">
          <Flex className="p-flex" align="center" justify="space-between">
            <Flex align="center" gap={3}>
              {resolvedActiveView === 'card' && (
                <Checkbox
                  checked={pagedFilteredData.length > 0 && selectedCardIds.length >= pagedFilteredData.length}
                  indeterminate={selectedCardIds.length > 0 && selectedCardIds.length < pagedFilteredData.length}
                  className="param-card-view__select-all"
                  onChange={() => {
                    if (selectedCardIds.length > 0) {
                      setSelectedCardIds([]);
                      setSelectedRowKeys([]);
                      setSelectedRows([]);
                    } else {
                      const allKeys = pagedFilteredData.map((record, idx) => String(getRowKey(record, idx, rowKey)));
                      setSelectedCardIds(allKeys);
                      setSelectedRowKeys(allKeys);
                      setSelectedRows([...pagedFilteredData]);
                    }
                  }}
                >
                  Select All
                </Checkbox>
              )}
              <Dropdown
                open={columnMenuOpen}
                onOpenChange={setColumnMenuOpen}
                trigger={['click']}
                dropdownRender={() => columnVisibilityContent}
                placement="bottomLeft"
                overlayClassName="column-visibility-dropdown"
              >
                <Button
                  aria-label="Display columns"
                  type="text"
                  icon={<img src={unorderedListOutlinedIcon} alt="" className="job-action-list-icon" />}
                  size="small"
                  className="job-actions-button job-column-display-button"
                >
                  <DownOutlined className="job-actions-caret" />
                </Button>
              </Dropdown>
                {selectedActionItems.length > 0 && selectedRowKeys.length > 0 && (
                  <Dropdown
                    menu={{
                      items: selectedActionItems.map(toMenuItem),
                      onClick: handleActionClick,
                    }}
                    trigger={['click']}
                  >
                    <Button
                      type="text"
                      size="small"
                      className="job-actions-button job-actions-menu-button"
                    >
                      <img src={frameIcon} alt="" className="job-actions-frame-icon" />
                      Actions <DownOutlined className="job-actions-caret" />
                    </Button>
                  </Dropdown>
                )}
                {selectedRowKeys.length > 0 && (
                  <Text className="job-selected-count">
                    Selected ({selectedRowKeys.length})
                  </Text>
                )}
              </Flex>
              {showSearchResultsCount && (
                <Text className="job-search-results-count">
                  Search Results ({searchResultsCount})
                </Text>
              )}
            </Flex>
          </Card>
        </>)}

      {hasActiveFieldFilters && (
        <FilterChipsBar
          filters={appliedFilterRows}
          onRemove={(originalIdx) => {
            const next = appliedFilterRows.filter((_, i) => i !== originalIdx);
            setAppliedFilterRows(next);
            resetPaginationPage();
          }}
          onClearAll={handleFilterReset}
        />
      )}

      <Spin spinning={isControlled ? propLoading : (configLoading || moduleLoading)}>
        {resolvedActiveView === 'card' ? (
          pagedFilteredData.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={emptyMessage}
              className="param-card-view__empty"
            >
              {!isControlled && canAdd && createRoute && !moduleError && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate(createRoute)}
                >
                  Add New
                </Button>
              )}
            </Empty>
          ) : (
            <div className="param-card-view__grid">
              {pagedFilteredData.map((record, idx) => {
                const key = String(getRowKey(record, idx, rowKey));
                return (
                  <RecordCard
                    key={key}
                    record={record}
                    fields={visibleCardFields}
                    selected={selectedCardIds.includes(key)}
                    isControlled={isControlled}
                    onPreview={() => handlePreviewOpen(record)}
                    onSelect={() => handleCardSelect(key)}
                  />
                );
              })}
            </div>
          )
        ) : (
          // <Table
          //   rowSelection={hasSourceRows ? {
          //     type: 'checkbox',
          //     selectedRowKeys,
          //     onChange: (keys, rows) => {
          //       setSelectedRowKeys(keys);
          //       setSelectedRows(rows);  
          //     },
          //     columnTitle: (() => {
          //       const totalOnPage = pagedFilteredData.length;
          //       const allSelected = totalOnPage > 0 && selectedRowKeys.length >= totalOnPage;
          //       const someSelected = selectedRowKeys.length > 0 && !allSelected;
          //       return (
          //         <Checkbox
          //           checked={allSelected}
          //           indeterminate={someSelected}
          //           onChange={() => {
          //             if (someSelected || allSelected) {
          //               setSelectedRowKeys([]);
          //               setSelectedRows([]);
          //             } else {
          //               const allKeys = pagedFilteredData.map((record, idx) => getRowKey(record, idx, rowKey));
          //               setSelectedRowKeys(allKeys);
          //               setSelectedRows([...pagedFilteredData]);
          //             }
          //           }}
          //         />
          //       );
          //     })(),
          //   } : undefined}
          //   columns={columns}
          //   dataSource={pagedFilteredData}
          //   size="middle"
          //   showSorterTooltip={false}
          //   tableLayout="fixed"
          //   scroll={(hasHorizontalScroll || isControlled) ? { x: 'max-content' } : undefined}
          //   pagination={false}
          //   onChange={handleTableChange}
          //   locale={{
          //     emptyText: (
          //       <Empty
          //         image={Empty.PRESENTED_IMAGE_SIMPLE}
          //         description={emptyMessage}
          //       />
          //     ),
          //   }}
          //   className={tableClassName}
          //   rowKey={(record, index) => getRowKey(record, index, rowKey)}
          // />
        <Table
          rowSelection={hasSourceRows ? {
            type: 'checkbox',
            fixed: true,
            columnWidth: 48,
            selectedRowKeys,
            onChange: (keys, rows) => {
              setSelectedRowKeys(keys);
              setSelectedRows(rows);
            },
            columnTitle: (() => {
              const totalOnPage = pagedFilteredData.length;
              const allSelected = totalOnPage > 0 && selectedRowKeys.length >= totalOnPage;
              const someSelected = selectedRowKeys.length > 0 && !allSelected;
              return (
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={() => {
                    if (someSelected || allSelected) {
                      setSelectedRowKeys([]);
                      setSelectedRows([]);
                    } else {
                      const allKeys = pagedFilteredData.map((record, idx) => getRowKey(record, idx, rowKey));
                      setSelectedRowKeys(allKeys);
                      setSelectedRows([...pagedFilteredData]);
                    }
                  }}
                />
              );
            })(),
          } : undefined}
          columns={columns}
          dataSource={pagedFilteredData}
          size="middle"
          showSorterTooltip={false}
          tableLayout="fixed"
          scroll={{ x: (hasHorizontalScroll || isControlled) ? 'max-content' : undefined, y: 'calc(100vh - 300px)' }}
          pagination={false}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={emptyMessage}
              />
            ),
          }}
          className={tableClassName}
          rowKey={(record, index) => getRowKey(record, index, rowKey)}
        />
        )}
      </Spin>

      {hasSourceRows ? <CustomPagination
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={tableTotal}
        onChange={(page, nextPageSize) => {
          if (isControlled && typeof onPageChange === 'function') {
            onPageChange(page, nextPageSize ?? pagination.pageSize);
            return;
          }
          setPaginationPage(page);
        }}
        onPageSizeChange={(size) => {
          if (isControlled && typeof onPageSizeChange === 'function') {
            onPageSizeChange(size);
            return;
          }
          setPagination({ current: 1, pageSize: size });
          updatePageRoute(1, { replace: true });
        }}
      /> : undefined}

      <DynamicFieldFilter
        moduleName={moduleName}
        open={filtersOpen}
        filters={appliedFilterRows}
        onClose={() => setFiltersOpen(false)}
        onApply={({ filters }) => {
          setAppliedFilterRows(filters);
          resetPaginationPage();
        }}
        onReset={handleFilterReset}
      />
      <ViewSummaryWidget
        moduleName={moduleName}
        open={summaryOpen}
        rows={moduleRows}
        tabs={listTabs}
        total={tableTotal}
        onClose={() => setSummaryOpen(false)}
      />
    </div>
  );
}
